'use client';
import React from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import { SUBJECTS, DEVELOPMENTAL_LEVELS, CAREER_PATHWAYS, type DevelopmentalLevel } from '@/lib/curriculum-data';

const LEVEL_ORDER: DevelopmentalLevel[] = ['early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate'];

const CAREER_LABEL_MAP: Record<string, { label: string; emoji: string }> = {
  medicine: { label: 'Medicine & Health Sciences', emoji: '⚕️' },
  engineering: { label: 'Engineering & Architecture', emoji: '⚙️' },
  ai_tech: { label: 'Technology & AI', emoji: '🤖' },
  entrepreneurship: { label: 'Entrepreneurship & Business', emoji: '🚀' },
  law: { label: 'Law & Policy', emoji: '⚖️' },
  islamic_scholarship: { label: 'Islamic Scholarship', emoji: '📿' },
  education: { label: 'Education & Teaching', emoji: '🎓' },
  global_dev: { label: 'Global Development', emoji: '🌍' },
  finance: { label: 'Finance & Economics', emoji: '💰' },
  arts_design: { label: 'Arts & Design', emoji: '🎨' },
};

export default function SubjectDetailPage() {
  const params = useParams();
  const id = params?.id as string;

  const subject = SUBJECTS.find(s => s.id === id);

  if (!subject) {
    return (
      <AppLayout activeRoute="/subject-hub">
        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
          <span className="text-6xl">📚</span>
          <h1 className="text-2xl font-800 text-foreground">Subject Not Found</h1>
          <p className="text-muted-foreground text-sm">The subject you're looking for doesn't exist.</p>
          <Link
            href="/subject-hub"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-600 hover:bg-primary/90 transition-colors"
          >
            ← Back to Subject Hub
          </Link>
        </div>
      </AppLayout>
    );
  }

  const subjectLevels = LEVEL_ORDER.filter(l => subject.levels.includes(l));

  return (
    <AppLayout activeRoute="/subject-hub">
      <div className="space-y-8 animate-fade-in">
        {/* Back Button */}
        <Link
          href="/subject-hub"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors font-600"
        >
          ← Back to Subject Hub
        </Link>

        {/* Hero Header */}
        <div className={`rounded-2xl bg-gradient-to-br ${subject.gradient} p-8 text-white shadow-lg`}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center text-5xl shadow-inner flex-shrink-0">
              {subject.emoji}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-800 leading-tight">{subject.name}</h1>
              {subject.nameAr && (
                <p className="text-lg opacity-80 mt-1 rtl-text">{subject.nameAr}</p>
              )}
              <p className="mt-3 text-white/90 text-sm leading-relaxed max-w-2xl">{subject.description}</p>
            </div>
            <div className="flex sm:flex-col gap-4 sm:gap-3 sm:text-right">
              <div className="bg-white/20 rounded-xl px-4 py-3 text-center">
                <p className="text-2xl font-800">{subject.totalLessons}</p>
                <p className="text-xs opacity-80">Lessons</p>
              </div>
              <div className="bg-white/20 rounded-xl px-4 py-3 text-center">
                <p className="text-2xl font-800">{subject.gameCount}</p>
                <p className="text-xs opacity-80">Games</p>
              </div>
            </div>
          </div>
        </div>

        {/* Curriculum Levels */}
        <div>
          <h2 className="text-lg font-800 text-foreground mb-4">Curriculum Levels</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {subjectLevels.map((level, idx) => {
              const info = DEVELOPMENTAL_LEVELS[level];
              return (
                <div
                  key={level}
                  className={`rounded-2xl p-4 border ${info.color} flex items-start gap-3`}
                >
                  <div className="text-2xl flex-shrink-0">{info.emoji}</div>
                  <div>
                    <p className="text-sm font-700 leading-tight">{info.label}</p>
                    <p className="text-xs mt-0.5 opacity-80">{info.ageRange}</p>
                    <p className="text-[10px] mt-1 opacity-60 font-600 uppercase tracking-wide">Level {LEVEL_ORDER.indexOf(level) + 1}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Level coverage indicator */}
          <div className="mt-4 flex items-center gap-2">
            <div className="flex gap-1">
              {LEVEL_ORDER.map(level => (
                <div
                  key={level}
                  className={`h-2 flex-1 rounded-full transition-all ${subject.levels.includes(level) ? `bg-gradient-to-r ${subject.gradient}` : 'bg-border'}`}
                  title={DEVELOPMENTAL_LEVELS[level].label}
                />
              ))}
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap font-600">
              {subjectLevels.length} / {LEVEL_ORDER.length} levels
            </span>
          </div>
        </div>

        {/* Career Connections */}
        <div>
          <h2 className="text-lg font-800 text-foreground mb-4">Career Connections</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {subject.careerConnections.map(careerId => {
              const meta = CAREER_LABEL_MAP[careerId] ?? { label: careerId, emoji: '💼' };
              const pathway = CAREER_PATHWAYS.find(p => p.id === careerId);
              return (
                <div
                  key={careerId}
                  className="bg-card border border-border rounded-2xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center text-xl flex-shrink-0">
                      {meta.emoji}
                    </div>
                    <h3 className="font-700 text-foreground text-sm leading-tight">{meta.label}</h3>
                  </div>
                  {pathway && (
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{pathway.description}</p>
                  )}
                  {pathway && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {pathway.coreSubjects.includes(subject.id) && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-600">Core Subject</span>
                      )}
                      {pathway.supportSubjects.includes(subject.id) && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border font-600">Support Subject</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className={`rounded-2xl bg-gradient-to-r ${subject.gradient} p-6 text-white flex flex-col sm:flex-row items-center justify-between gap-4`}>
          <div>
            <h3 className="font-800 text-lg">Ready to explore {subject.name}?</h3>
            <p className="text-sm opacity-80 mt-1">{subject.totalLessons} lessons across {subjectLevels.length} developmental levels</p>
          </div>
          <Link
            href="/curriculum-course-explorer"
            className="flex-shrink-0 bg-white text-foreground font-700 text-sm px-5 py-2.5 rounded-xl hover:bg-white/90 transition-colors shadow-sm"
          >
            Browse Lessons →
          </Link>
        </div>
      </div>
    </AppLayout>
  );
}
